import { Box, Typography, styled, Breadcrumbs, Link, useTheme, alpha, CardHeaderProps } from '@mui/material';
import { Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import pages from 'src/router/routes';
import { ReactNode } from 'react';
import { Link as RouterLink, Route, Routes, MemoryRouter, useLocation } from 'react-router-dom';

const PageContainer = styled(Box)(
  ({ theme }) => `
    padding: ${theme.spacing(1)};
    padding-top: ${theme.spacing(3)};
    min-height: 100vh;
    background: linear-gradient(135deg, 
      ${alpha(theme.colors.primary.main, 0.02)} 0%, 
      ${alpha(theme.colors.primary.light, 0.05)} 100%);
  `,
);

const BreadcrumbContainer = styled(Box)(
  ({ theme }) => `
    margin-bottom: ${theme.spacing(2)};
  `,
);

const PageTitle = styled(Typography)(
  ({ theme }) => `
    font-weight: 700;
    font-size: 2.15rem;
    background: linear-gradient(135deg, ${theme.colors.primary.main}, ${theme.colors.primary.dark});
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: ${theme.spacing(1)};
  `,
);

const PageHeader = styled(Box)(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: ${theme.spacing(2)};
    gap: ${theme.spacing(2)};
    
    @media (max-width: ${theme.breakpoints.values.sm}px) {
      flex-direction: column;
      align-items: flex-start;
      gap: ${theme.spacing(1)};
    }
  `,
);

const TitleSection = styled(Box)`
  flex: 1;
  min-width: 0; // Allow text truncation
`;

const ActionsSection = styled(Box)(
  ({ theme }) => `
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    
    @media (max-width: ${theme.breakpoints.values.sm}px) {
      width: 100%;
      justify-content: flex-end;
    }
  `,
);

const PageSubtitle = styled(Typography)(
  ({ theme }) => `
    color: ${theme.colors.alpha.black[70]};
    font-size: 0.9rem;
    font-weight: 400;
    
  `,
);

interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
}

interface PageWrapperProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  showHomeBreadcrumb?: boolean;
  action?: ReactNode;
}

export const PageWrapper = ({
  children,
  title,
  subtitle,
  breadcrumbs = [],
  showHomeBreadcrumb = true,
  action,
}: PageWrapperProps) => {
  const theme = useTheme();

  const allBreadcrumbs = showHomeBreadcrumb
    ? [{ label: 'Dashboard', href: pages.home.path, icon: <HomeIcon fontSize='small' /> }, ...breadcrumbs]
    : breadcrumbs;

  return (
    <PageContainer>
      {/* Page Header with Breadcrumbs */}
      {allBreadcrumbs.length > 0 && (
        <BreadcrumbContainer>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize='small' />}
            sx={{
              '& .MuiBreadcrumbs-li': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            {allBreadcrumbs.map((breadcrumb, index) => {
              const isLast = index === allBreadcrumbs.length - 1;
              const key = `${breadcrumb.label}-${breadcrumb.href || 'current'}`;

              if (isLast || !breadcrumb.href) {
                return (
                  <Typography
                    key={key}
                    color='text.primary'
                    sx={{
                      fontWeight: 600,
                      color: theme.colors.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    {breadcrumb.icon}
                    {breadcrumb.label}
                  </Typography>
                );
              }

              return (
                <Link
                  key={key}
                  component={RouterLink as any}
                  to={breadcrumb.href}
                  color='inherit'
                  underline='hover'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: theme.colors.alpha.black[70],
                    '&:hover': {
                      color: theme.colors.primary.main,
                    },
                  }}
                >
                  {breadcrumb.icon}
                  {breadcrumb.label}
                </Link>
              );
            })}
          </Breadcrumbs>
        </BreadcrumbContainer>
      )}

      {/* Page Header with Title and Actions */}
      {title && (
        <PageHeader>
          <TitleSection>
            <PageTitle variant='h1'>{title}</PageTitle>
            {subtitle && <PageSubtitle>{subtitle}</PageSubtitle>}
          </TitleSection>
          {action && <ActionsSection>{action}</ActionsSection>}
        </PageHeader>
      )}

      {children}
    </PageContainer>
  );
};
